import React, { useEffect, useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Button, Container, Row, Col, Card, CardBody } from 'reactstrap';
import { Link } from 'react-router-dom';
import PreviewCardHeader from '../../../../components/common/PreviewCardHeader';
import TableContainer from '../../../../components/common/TableContainer';
import { FaRegSadCry } from 'react-icons/fa';
import Loader from '../../../../components/common/Loader';
import showConfirmationAlert from '../../../../components/custom/confirmationAlert';
import OohLocationAudienceModal from './OohLocationAudienceModal';
import { showErrorAlert, showSuccessAlert , showLoadingAlert } from "../../../../components/custom/sweetalertHelper";  

// Import the actions
import { getOohList } from '../../../../slices/veloxity/ooh/OohListReducer';
import { createOohAudience } from '../../../../slices/veloxity/ooh/OohCreateReducer';
import { deleteOohAudience } from '../../../../slices/veloxity/ooh/OohDeleteReducer';

const OohLocationAudience = () => {
  const dispatch = useDispatch();


  const { loading, payload: audienceData, error } = useSelector(state => state.oohList);

  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    dispatch(getOohList());
  }, [dispatch]);

  const toggleModal = () => {
    setIsModalOpen(!isModalOpen);
  };


const formatDateToLocalDateTime = (dateString) => {
  const date = new Date(dateString);
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}T${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}:${String(date.getSeconds()).padStart(2, '0')}`;
};


const handleCreateAudience = async (name, csvFile, startDate, endDate) => {
  const formattedStartDate = formatDateToLocalDateTime(startDate);
  const formattedEndDate = formatDateToLocalDateTime(endDate);

  showLoadingAlert("Creating OOH Location Audience...");

  try {
    const response = await dispatch(createOohAudience({ name, csvFile, startDate: formattedStartDate, endDate: formattedEndDate }));

    if (response.meta.requestStatus === 'fulfilled') {
      showSuccessAlert("OOH Location Audience created successfully!");
      dispatch(getOohList()); 
    } else {
      showErrorAlert("Failed to create OOH Location Audience", response.error.message);
    }
  } catch (error) {
    showErrorAlert("An unexpected error occurred", error.message);
  }
};

const handleDownload = (url) => {
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', 'ooh_audience_data.csv'); // Optional: Set the file name
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link); // Cleanup
};


  const handleDelete = async (id, audienceName) => {
    showConfirmationAlert(`Are you sure you want to delete the OOH Location Audience "${audienceName}"?`, async () => {
      const response = await dispatch(deleteOohAudience(id));

      if (response.meta.requestStatus === 'fulfilled') {
        dispatch(getOohList()); 
      }
    });
  };

  const getStatusStyle = (status) => {
    switch (status) {
      case 'SUCCEEDED':
        return { color: 'success', text: 'Confirmed' };
      case 'CALCULATING':
      case 'SCHEDULED':
        return { color: 'warning', text: 'In Progress' };
      case 'FAILED':
        return { color: 'danger', text: 'Failed' };
      default:
        return { color: 'secondary', text: 'Unknown' };
    }
  };

  const columns = useMemo(
    () => [
      {
        Header: "OOH Location Audience",
        accessor: "name",
        width: 200,
      },
      {
        Header: "Process",
        id: "process", 
        accessor: "status",
        width: 150,
        Cell: ({ cell }) => {
          const statusStyle = getStatusStyle(cell.value);
          return (
            <div className="progress progress-sm">
              <div
                className={`progress-bar bg-${statusStyle.color}`}
                role="progressbar"
                style={{ width: cell.value === 'SUCCEEDED' ? '100%' : '50%' }}
                aria-valuenow={cell.value === 'SUCCEEDED' ? 100 : 50}
                aria-valuemin="0"
                aria-valuemax="100"
              ></div>
            </div>
          );
        },
      },
      {
        Header: "Status",
        id: "statusText", 
        accessor: "status",
        width: 120,
        Cell: ({ cell }) => {
          const statusStyle = getStatusStyle(cell.value);
          return (
            <Link to="#" className={`link-${statusStyle.color}`}>
              {statusStyle.text}
            </Link>
          );
        },
      },
      {
        Header: "Creation Date",
        accessor: "createdAt", 
        Cell: ({ cell }) => {
          const date = new Date(cell.value);
          return date.toLocaleDateString('en-GB', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
          });
        },
      },
      {
        Header: "Action",
        id: "action",
        Cell: ({ row }) => (
          <>
            <Link to="#">
              <i
                className={`ri-download-2-line fs-17 lh-1 align-middle ${row.original.status !== 'SUCCEEDED' ? 'text-muted' : ''}`}
                onClick={() => {
                  if (row.original.status === 'SUCCEEDED' && row.original.downloadable) {
                    handleDownload(row.original.calculatedOohUrl);
                  }
                }}
                style={{ pointerEvents: row.original.status !== 'SUCCEEDED' ? 'none' : 'auto', marginRight: '10px' }}
              ></i>
            </Link>
            <Link to="#">
              <i
                className={`ri-delete-bin-line fs-17 lh-1 align-middle ${row.original.status !== 'SUCCEEDED' ? 'text-muted' : 'text-danger'}`}
                onClick={() => {
                  if (row.original.status === 'SUCCEEDED') {
                    handleDelete(row.original.id, row.original.name);
                  }
                }}
                style={{ pointerEvents: row.original.status !== 'SUCCEEDED' ? 'none' : 'auto', marginLeft: '10px' }}
              ></i>
            </Link>
          </>
        ),
      },
    ],
    [dispatch]
  );

  return (
    <div className="page-content">
      <Container fluid>
        <Row>
          <Card
            className="p-3"
            style={{
              boxShadow: "2px 2px 5px rgba(0, 0, 0, 0.2)",
              marginBottom: "30px",
              marginRight: "5%",
              width: "110%",
              height: "90%"
            }}
          >
            <Row>
              <Col className="d-flex justify-content-start">
                <Button color="primary" style={{ maxWidth: "190px" }} onClick={toggleModal}>
                  Create New
                </Button>
              </Col>
            </Row>
          </Card>
        </Row>
        <Row>
          <Card>
            <PreviewCardHeader title="OOH LOCATION AUDIENCE" />
            <CardBody>
              {loading ? (
                <div className="text-center">
                  <Loader />
                </div>
              ) : error ? (
                <div className="text-center text-danger">
                  <p>Error: {error}</p>
                </div>
              ) : audienceData.length === 0 ? (
                <div className="text-center">
                  <FaRegSadCry size={50} color="gray" />
                  <h4 className="mt-3">No OOH Location Audience Data Available</h4>
                  <p className="text-muted">It looks like you haven't created any OOH Location Audience data yet. Start by creating your first OOH Location Audience.</p>
                  <Button color="primary" onClick={toggleModal}>
                    Create New OOH Location Audience
                  </Button>
                </div>
              ) : (
                <div style={{ overflowX: 'auto' }}>
                  <TableContainer
                    columns={columns}
                    data={audienceData}
                    isGlobalFilter={true}
                    customPageSize={5}
                    SearchPlaceholder="Search OOH Location Audience..."
                    tableClass="table-centered align-middle table-nowrap mb-0"
                    theadClass="text-muted table-light"
                    style={{ width: '100%' }}
                  />
                </div>
              )}
            </CardBody>
          </Card>
        </Row>
      </Container>


      <OohLocationAudienceModal
        isOpen={isModalOpen}
        toggle={toggleModal}
        onCreate={handleCreateAudience}
      />
    </div>
  );
};

export default OohLocationAudience;
